import { useState, useCallback } from "react";
import { Calculator as CalcIcon, DollarSign, ArrowRight, Phone, Info, ChevronDown } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { calculate } from "@/lib/title-calculator/engine";

const STATES = [
  { code: "PA", label: "Pennsylvania" },
  { code: "NJ", label: "New Jersey" },
  { code: "NY", label: "New York" },
  { code: "FL", label: "Florida" },
];

const TRANSACTION_TYPES = [
  { value: "purchase", label: "Purchase" },
  { value: "refinance", label: "Refinance" },
];

const NY_ZONES = [
  { value: "zone2", label: "Downstate / NYC / Long Island" },
  { value: "zone1", label: "Upstate / Western NY" },
];

const FL_PRIOR_LOAN_OPTIONS = [
  { value: 3, label: "3 years or under" },
  { value: 4, label: "Over 3 to 4 years" },
  { value: 5, label: "Over 4 to 5 years" },
  { value: 10, label: "Over 5 to 10 years" },
  { value: 99, label: "Over 10 years" },
];

function formatCurrency(num) {
  if (num === null || num === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function parseCurrencyInput(value) {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.round(num * 100) / 100;
}

function formatInputDisplay(value) {
  if (!value && value !== 0) return "";
  const num = typeof value === "string" ? parseCurrencyInput(value) : value;
  if (num === 0) return "";
  return num.toLocaleString("en-US");
}

export const Calculator = () => {
  const [state, setState] = useState("PA");
  const [transactionType, setTransactionType] = useState("purchase");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [zone, setZone] = useState("zone2");
  const [priorLoanYears, setPriorLoanYears] = useState(3);
  const [result, setResult] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const isPurchase = transactionType === "purchase";

  const resetForm = useCallback(() => {
    setPurchasePrice("");
    setLoanAmount("");
    setResult(null);
    setHasCalculated(false);
  }, []);

  const handleStateChange = (newState) => {
    setState(newState);
    resetForm();
  };

  const handleTransactionChange = (type) => {
    setTransactionType(type);
    resetForm();
  };

  const handleCalculate = (e) => {
    e.preventDefault();
    const pp = parseCurrencyInput(purchasePrice);
    const la = parseCurrencyInput(loanAmount);

    const inputs = {
      transactionType,
      purchasePrice: pp,
      loanAmount: la,
      zone,
      priorLoanYears: Number(priorLoanYears),
    };

    try {
      const res = calculate(state, inputs);
      setResult(res);
      setHasCalculated(true);
    } catch (err) {
      console.error(err);
    }
  };

  const canCalculate = isPurchase
    ? parseCurrencyInput(purchasePrice) > 0
    : parseCurrencyInput(loanAmount) > 0;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />
      <Toaster position="bottom-right" />

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(circle at 25% 25%, rgba(180,83,9,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(100,116,139,0.2) 0%, transparent 50%)"
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <p className="text-xs font-bold uppercase tracking-widest text-[#B45309] mb-4">
            Title Insurance Calculator
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight mb-6">
            Estimate Your Premium
          </h1>
          <p className="text-lg text-[#94a3b8] max-w-2xl leading-relaxed">
            Get an instant estimate for title insurance premiums in Pennsylvania, New Jersey,
            New York, and Florida. Select your state and transaction type below.
          </p>
        </div>
      </section>

      {/* Calculator Body */}
      <section className="py-16 lg:py-24 noise-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">

            {/* Left Column - Form (3/5) */}
            <div className="lg:col-span-3">
              <div className="bg-white p-8 lg:p-10 border border-[#E7E5E4] card-shadow">

                {/* State Selector */}
                <div className="mb-8">
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">
                    State
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {STATES.map((s) => (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => handleStateChange(s.code)}
                        className={`px-4 py-3 text-sm font-semibold btn-transition border ${
                          state === s.code
                            ? "bg-[#0F172A] text-white border-[#0F172A]"
                            : "bg-transparent text-[#64748B] border-[#E7E5E4] hover:border-[#0F172A] hover:text-[#0F172A]"
                        }`}
                      >
                        {s.code}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Transaction Type */}
                <div className="mb-8">
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">
                    Transaction Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TRANSACTION_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => handleTransactionChange(t.value)}
                        className={`px-4 py-3 text-sm font-semibold btn-transition border ${
                          transactionType === t.value
                            ? "bg-[#0F172A] text-white border-[#0F172A]"
                            : "bg-transparent text-[#64748B] border-[#E7E5E4] hover:border-[#0F172A] hover:text-[#0F172A]"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* NY Zone Selector */}
                {state === "NY" && (
                  <div className="mb-8">
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      Rate Zone
                    </label>
                    <div className="relative">
                      <select
                        value={zone}
                        onChange={(e) => { setZone(e.target.value); resetForm(); }}
                        className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] appearance-none cursor-pointer pr-8"
                      >
                        {NY_ZONES.map((z) => (
                          <option key={z.value} value={z.value}>{z.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* FL Prior Loan Years (refinance only) */}
                {state === "FL" && !isPurchase && (
                  <div className="mb-8">
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      Age of Prior Loan
                    </label>
                    <div className="relative">
                      <select
                        value={priorLoanYears}
                        onChange={(e) => setPriorLoanYears(Number(e.target.value))}
                        className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] appearance-none cursor-pointer pr-8"
                      >
                        {FL_PRIOR_LOAN_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
                    </div>
                  </div>
                )}

                <form onSubmit={handleCalculate}>
                  {/* Purchase Price (purchase only) */}
                  {isPurchase && (
                    <div className="mb-6">
                      <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                        Purchase Price *
                      </label>
                      <div className="relative">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formatInputDisplay(purchasePrice)}
                          onChange={(e) => setPurchasePrice(e.target.value.replace(/[^0-9.]/g, ""))}
                          placeholder="350,000"
                          className="w-full pl-4 pr-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] text-lg focus:border-[#0F172A] placeholder:text-[#94a3b8]"
                        />
                      </div>
                    </div>
                  )}

                  {/* Loan Amount */}
                  <div className="mb-8">
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      Loan Amount {!isPurchase && "*"} {isPurchase && "(optional)"}
                    </label>
                    <div className="relative">
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatInputDisplay(loanAmount)}
                        onChange={(e) => setLoanAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder={isPurchase ? "280,000" : "350,000"}
                        className="w-full pl-4 pr-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] text-lg focus:border-[#0F172A] placeholder:text-[#94a3b8]"
                      />
                    </div>
                    {isPurchase && (
                      <p className="text-xs text-[#94a3b8] mt-1">
                        Enter to calculate simultaneous lender's policy premium
                      </p>
                    )}
                  </div>

                  {/* Calculate Button */}
                  <button
                    type="submit"
                    disabled={!canCalculate}
                    className="w-full flex items-center justify-center gap-3 bg-[#0F172A] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#1e293b] btn-transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
                    Calculate Premium
                  </button>
                </form>
              </div>

              {/* State Info Note */}
              <div className="mt-6 flex items-start gap-3 text-sm text-[#64748B]">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p>
                  {state === "PA" && "Pennsylvania uses an all-inclusive rate. One premium covers both owner's and lender's policies on purchase transactions."}
                  {state === "NJ" && "New Jersey charges a $100.00 Search & Examination fee per policy in addition to the underwriting premium."}
                  {state === "NY" && "New York rates vary by zone. Downstate (NYC, Long Island, Westchester, Rockland, Putnam) uses Zone 2 rates."}
                  {state === "FL" && "Florida uses statewide promulgated rates set by the Office of Insurance Regulation. Refinance discounts depend on the age of the prior loan."}
                </p>
              </div>
            </div>

            {/* Right Column - Results (2/5) */}
            <div className="lg:col-span-2">
              {!hasCalculated ? (
                <div className="bg-white p-8 lg:p-10 border border-[#E7E5E4] card-shadow h-full flex flex-col items-center justify-center text-center min-h-[400px]">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FAFAF9] text-[#64748B] mb-6 border border-[#E7E5E4]">
                    <DollarSign className="w-7 h-7" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-[#0F172A] mb-2">
                    Your Estimate
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    Enter your transaction details and click calculate to see an instant premium estimate.
                  </p>
                </div>
              ) : result && (
                <div className="bg-white border border-[#E7E5E4] card-shadow animate-fade-in">
                  {/* Results Header */}
                  <div className="bg-[#0F172A] px-8 py-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#B45309] mb-1">
                      Estimated Total Premium
                    </p>
                    <p className="font-serif text-4xl font-medium text-white">
                      {formatCurrency(result.totalPremium)}
                    </p>
                    <p className="text-sm text-[#94a3b8] mt-1">
                      {result.state} &middot; {isPurchase ? "Purchase" : "Refinance"}
                    </p>
                  </div>

                  {/* Breakdown */}
                  <div className="px-8 py-6 space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">
                      Premium Breakdown
                    </h4>

                    {result.ownerPremium !== null && (
                      <div className="flex items-center justify-between py-2 border-b border-[#E7E5E4]">
                        <span className="text-sm text-[#64748B]">Owner's Policy</span>
                        <span className="text-sm font-semibold text-[#0F172A]">
                          {formatCurrency(result.ownerPremium)}
                        </span>
                      </div>
                    )}

                    {result.lenderPremium !== null && result.lenderPremium > 0 && (
                      <div className="flex items-center justify-between py-2 border-b border-[#E7E5E4]">
                        <span className="text-sm text-[#64748B]">Lender's Policy</span>
                        <span className="text-sm font-semibold text-[#0F172A]">
                          {formatCurrency(result.lenderPremium)}
                        </span>
                      </div>
                    )}

                    {isPurchase && result.lenderPremium === 0 && result.ownerPremium !== null && (
                      <div className="flex items-center justify-between py-2 border-b border-[#E7E5E4]">
                        <span className="text-sm text-[#64748B]">Lender's Policy</span>
                        <span className="text-sm font-semibold text-green-700">Included</span>
                      </div>
                    )}

                    {result.lenderPremium === null && !isPurchase && (
                      <div className="flex items-center justify-between py-2 border-b border-[#E7E5E4]">
                        <span className="text-sm text-[#64748B]">Lender's Policy</span>
                        <span className="text-sm font-semibold text-[#0F172A]">
                          {formatCurrency(result.totalPremium)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-bold text-[#0F172A]">Total</span>
                      <span className="text-sm font-bold text-[#0F172A]">
                        {formatCurrency(result.totalPremium)}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {(result.notes.length > 0 || result.simultaneousNote) && (
                    <div className="px-8 py-6 bg-[#FAFAF9] border-t border-[#E7E5E4]">
                      <h4 className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">
                        Notes
                      </h4>
                      <ul className="space-y-2">
                        {result.simultaneousNote && (
                          <li className="text-xs text-[#64748B] leading-relaxed flex items-start gap-2">
                            <span className="text-[#B45309] mt-0.5">&#8226;</span>
                            {result.simultaneousNote}
                          </li>
                        )}
                        {result.notes.map((note, i) => (
                          <li key={i} className="text-xs text-[#64748B] leading-relaxed flex items-start gap-2">
                            <span className="text-[#B45309] mt-0.5">&#8226;</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="px-8 py-6 border-t border-[#E7E5E4]">
                    <a
                      href="/#contact"
                      className="w-full flex items-center justify-center gap-3 bg-[#B45309] text-white px-6 py-4 text-xs font-bold uppercase tracking-widest hover:bg-[#92400e] btn-transition"
                    >
                      <Phone className="w-4 h-4" strokeWidth={1.5} />
                      Get an Exact Quote
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-16 p-8 bg-white border border-[#E7E5E4]">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-4">
              Important Disclaimer
            </h3>
            <div className="space-y-3 text-xs text-[#64748B] leading-relaxed">
              <p>
                This calculator provides <strong className="text-[#0F172A]">estimates only</strong> and
                is intended for informational purposes. Actual premiums may vary based on factors
                including but not limited to: endorsements, property type, prior policy availability,
                reissue credits, special risk assessments, and title search complexity.
              </p>
              <p>
                Rates are based on currently filed rate schedules with the respective state insurance
                departments. Pennsylvania rates per TIRBOP Manual (eff. 08/01/2024). New Jersey rates
                per NJLTA Rating Bureau (eff. 11/01/2023). New York rates per TIRSA Rate Manual
                (eff. 10/01/2024). Florida rates per FAC Rule 69O-186.003.
              </p>
              <p>
                This tool does not constitute a commitment to insure, a rate guarantee, or legal advice.
                For a binding quote tailored to your specific transaction, please{" "}
                <a href="/#contact" className="text-[#B45309] hover:underline font-semibold">
                  contact us directly
                </a>{" "}
                or call{" "}
                <a href="tel:8003057119" className="text-[#B45309] hover:underline font-semibold">
                  (800) 305-7119
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
